import { useEffect, useState } from 'react';
import { useUser } from '@/hooks/auth';
import { supabase } from '@/integrations/supabase/client';

export interface UserExperience {
  id: string;
  user_id: string;
  company: string;
  position: string;
  location?: string;
  employment_type?: string;
  start_date?: string;
  end_date?: string;
  is_current?: boolean;
  description?: string;
  created_at: string;
  updated_at: string;
}

export const useUserExperiences = (userId?: string) => {
  const { user } = useUser();
  const [experiences, setExperiences] = useState<UserExperience[]>([]);
  const [loading, setLoading] = useState(true);

  const targetUserId = userId || user?.id;

  useEffect(() => {
    if (!targetUserId) {
      setExperiences([]);
      setLoading(false);
      return;
    }

    const fetchExperiences = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('user_experiences')
          .select('*')
          .eq('user_id', targetUserId as any)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching experiences:', error);
          return;
        }

        setExperiences((data as any) || []);
      } catch (error) {
        console.error('Error fetching experiences:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchExperiences();

    // Listen for profile data updates
    const handleProfileUpdate = () => {
      fetchExperiences();
    };

    window.addEventListener('profileDataUpdated', handleProfileUpdate);

    return () => {
      window.removeEventListener('profileDataUpdated', handleProfileUpdate);
    };
  }, [targetUserId]);

  const addExperience = async (experience: Omit<UserExperience, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    if (!user?.id) return;

    try {
        const { data, error } = await supabase
          .from('user_experiences')
          .insert({ ...experience, user_id: user.id } as any)
          .select()
          .single();

        if (error) throw error;

        setExperiences(prev => [(data as any), ...prev]);
      return data;
    } catch (error) {
      console.error('Error adding experience:', error);
      throw error;
    }
  };

  const updateExperience = async (id: string, updates: Partial<UserExperience>) => {
    try {
        const { data, error } = await supabase
          .from('user_experiences')
          .update(updates as any)
          .eq('id', id as any)
          .select()
          .single();

        if (error) throw error;

        setExperiences(prev => prev.map(e => e.id === id ? (data as any) : e));
      return data;
    } catch (error) {
      console.error('Error updating experience:', error);
      throw error;
    }
  };

  const deleteExperience = async (id: string) => {
    try {
      const { error } = await supabase
        .from('user_experiences')
        .delete()
        .eq('id', id as any);

      if (error) throw error;

      setExperiences(prev => prev.filter(e => e.id !== id));
    } catch (error) {
      console.error('Error deleting experience:', error);
      throw error;
    }
  };

  return {
    experiences,
    loading,
    addExperience,
    updateExperience,
    deleteExperience
  };
};